import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TodayIcon from '@mui/icons-material/Today'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isSunday,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useDueFollowups,
  useMuhurthamDates,
  useTamilDates,
  useOrderCalendar,
  useOrders,
  useOutstandingReport,
} from '@/api/hooks'
import type { MuhurthamDate, OrderSummary, TamilDate } from '@/api/types'
import { MuhurthamDialog } from './desk/MuhurthamDialog'
import { MUHURTHAM_GOLD, MuhurthamMark, MoonMark } from '@/components/CalendarMarks'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMobile,
  formatMoney,
  humanise,
  LoadingBlock,
  PageHeader,
  StatusChip,
} from '@/components/common'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * A day is square, and the same size as a day on the hall availability
 * calendar: someone reading both in a morning should not have to adjust to two
 * different grids.
 *
 * <p>The columns are held to this height rather than stretched across the card,
 * and the card is then narrowed to the calendar so no dead space is left beside
 * it.</p>
 *
 * <p>Text does not scale with the cell; the type keeps a legible floor and the
 * cell sizes around it.</p>
 */
/**
 * The ground a day sits on: sandalwood.
 *
 * <p>Warm rather than white, which is what the office asked for and what stops
 * a month of empty cells reading as a blank sheet. Kept pale so everything laid
 * on it still carries: a muhurtham keeps its own tint against this, an order
 * count stays readable, and the selected day stays obviously selected.</p>
 *
 * <p>In the dark theme the same warmth at a fraction of the strength - a beige
 * panel at full weight in a dark room is a lamp, not a background.</p>
 */
const sandal = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? alpha('#E0C9A6', 0.08)
    : '#F5EBD9'

const DAY_CELL_HEIGHT = 64
const DAY_GRID_GAP = 3

/**
 * Seven square days, the gaps between them and the card's own padding - so the
 * card ends where the calendar does instead of trailing empty space beside it.
 */
const CALENDAR_WIDTH = 7 * DAY_CELL_HEIGHT + 6 * DAY_GRID_GAP + 32

/** The four lines of business, in the order the operators think of them. */
const SERVICE_TYPES: { key: string; label: string }[] = [
  { key: 'BANQUET', label: 'Banquet' },
  { key: 'OUTDOOR_CATERING', label: 'Outdoor catering' },
  { key: 'RESTAURANT_PARTY', label: 'Restaurant' },
  { key: 'BULK_TAKEAWAY', label: 'Bulk takeaway' },
]

/**
 * The screen someone opens first thing: what is happening this month, what is
 * coming next, who owes money and who needs a call today.
 */
/**
 * The three marks a salesperson reads a date by.
 *
 * <p>Muhurtham fills the halls, Pournami fills the temples and the outdoor
 * catering book behind them, and Amavasai empties both - no wedding is fixed on
 * a new moon. They are drawn rather than written out so a month of them can be
 * taken in at a glance.</p>
 */
const MARKS = {
  muhurtham: { symbol: <MuhurthamMark />, colour: MUHURTHAM_GOLD, title: 'Muhurtham' },
  POURNAMI: {
    symbol: <MoonMark phase="POURNAMI" />,
    colour: 'text.primary',
    title: 'Pournami - full moon',
  },
  AMAVASAI: {
    symbol: <MoonMark phase="AMAVASAI" />,
    colour: 'text.primary',
    title: 'Amavasai - new moon',
  },
} as const

export function MyDeskPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)

  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const [selected, setSelected] = useState<Date | null>(new Date())
  const [mineOnly, setMineOnly] = useState(false)
  const [loadingMuhurthams, setLoadingMuhurthams] = useState(false)

  // The grid runs whole weeks, so it always starts on a Monday.
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 })

  const { data: calendar, isLoading, error } = useOrderCalendar(
    format(gridStart, 'yyyy-MM-dd'),
    format(gridEnd, 'yyyy-MM-dd'),
  )
  const { data: muhurthams } = useMuhurthamDates(
    format(gridStart, 'yyyy-MM-dd'),
    format(gridEnd, 'yyyy-MM-dd'),
  )
  const { data: tamilDates } = useTamilDates(
    format(gridStart, 'yyyy-MM-dd'),
    format(gridEnd, 'yyyy-MM-dd'),
  )
  // The events panel answers "what is on this day", so it is read for the day
  // picked on the calendar. The calendar feed itself cannot serve it: that feed
  // leaves cancelled orders out, and a cancelled booking is exactly what the
  // office needs to see before it offers the slot to someone else.
  const selectedDay = selected ? format(selected, 'yyyy-MM-dd') : undefined
  const { data: dayOrdersPage } = useOrders({ from: selectedDay, to: selectedDay, size: 50 })
  const { data: outstanding } = useOutstandingReport(can('billing.read'))
  const { data: followups } = useDueFollowups(
    mineOnly && user ? { assignedTo: user.id } : undefined,
  )

  const days = useMemo(() => eachDayOfInterval({ start: gridStart, end: gridEnd }), [gridStart, gridEnd])

  /** Orders keyed by their event day, so a cell is a single lookup. */
  const byDay = useMemo(() => {
    const map = new Map<string, OrderSummary[]>()
    for (const order of calendar ?? []) {
      const key = order.eventDate.slice(0, 10)
      const existing = map.get(key)
      if (existing) existing.push(order)
      else map.set(key, [order])
    }
    return map
  }, [calendar])

  const muhurthamByDay = useMemo(() => {
    const map = new Map<string, MuhurthamDate>()
    for (const entry of muhurthams ?? []) map.set(entry.date.slice(0, 10), entry)
    return map
  }, [muhurthams])

  const tamilByDay = useMemo(() => {
    const map = new Map<string, TamilDate>()
    for (const entry of tamilDates ?? []) map.set(entry.date.slice(0, 10), entry)
    return map
  }, [tamilDates])

  const ordersOn = (day: Date) => byDay.get(format(day, 'yyyy-MM-dd')) ?? []
  const muhurthamOn = (day: Date) => muhurthamByDay.get(format(day, 'yyyy-MM-dd'))
  const tamilOn = (day: Date) => tamilByDay.get(format(day, 'yyyy-MM-dd'))
  const selectedMuhurtham = selected ? muhurthamOn(selected) : undefined

  /** Orders in the displayed month, split by line of business. */
  const countsByService = useMemo(() => {
    const counts = new Map<string, number>()
    for (const order of calendar ?? []) {
      if (!isSameMonth(parseISO(order.eventDate), month)) continue
      counts.set(order.serviceType, (counts.get(order.serviceType) ?? 0) + 1)
    }
    return counts
  }, [calendar, month])

  /**
   * Everything on the selected day, which is now read in one place.
   *
   * <p>Every status, not only the confirmed ones: a draft still to be chased
   * and a cancellation that has freed the slot are both things the office needs
   * to see when it looks at a date. Confirmed first, cancelled last, since that
   * is the order they are acted on in.</p>
   */
  const dayEvents = (dayOrdersPage?.content ?? [])
    .slice()
    .sort((a, b) => {
      const rank = (status: string) =>
        status === 'CONFIRMED' ? 0 : status === 'CANCELLED' ? 2 : 1
      return rank(a.status) - rank(b.status) || a.orderNumber.localeCompare(b.orderNumber)
    })

  return (
    <>
      <PageHeader
        title={user ? `Good to see you, ${user.fullName.split(' ')[0]}` : 'My desk'}
        subtitle="Your events, what is coming, who owes money and who needs a call"
        actions={
          <Button
            size="small"
            variant={mineOnly ? 'contained' : 'outlined'}
            onClick={() => setMineOnly((current) => !current)}
          >
            {mineOnly ? 'My follow-ups' : 'All follow-ups'}
          </Button>
        }
      />

      <ErrorBanner error={error} />

      <MuhurthamDialog
        open={loadingMuhurthams}
        onClose={() => setLoadingMuhurthams(false)}
        existing={muhurthams ?? []}
        monthLabel={format(month, 'MMMM yyyy')}
      />

      <Grid container spacing={2}>
        {/* ------------------------------------------------------- calendar */}
        <Grid item xs={12} lg="auto">
          <Card sx={{ width: { lg: CALENDAR_WIDTH } }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">{format(month, 'MMMM yyyy')}</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Today">
                    <IconButton
                      size="small"
                      aria-label="Today"
                      onClick={() => {
                        setMonth(startOfMonth(new Date()))
                        setSelected(new Date())
                      }}
                    >
                      <TodayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    aria-label="Previous month"
                    onClick={() => setMonth((m) => addMonths(m, -1))}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Next month"
                    onClick={() => setMonth((m) => addMonths(m, 1))}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Orders in view, split by line of business. */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {SERVICE_TYPES.map((service) => {
                  const count = countsByService.get(service.key) ?? 0
                  return (
                    <Chip
                      key={service.key}
                      size="small"
                      variant={count > 0 ? 'filled' : 'outlined'}
                      color={count > 0 ? 'primary' : 'default'}
                      label={`${service.label} ${count}`}
                    />
                  )
                })}
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                  size="small"
                  variant="outlined"
                  color="secondary"
                  label={`Muhurtham ${(muhurthams ?? []).length}`}
                  onClick={canWrite('config') ? () => setLoadingMuhurthams(true) : undefined}
                  title={
                    canWrite('config')
                      ? 'Load muhurtham dates from your panchangam'
                      : undefined
                  }
                />
              </Stack>

              {/* What the marks mean, so the calendar needs no explaining. */}
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                {([MARKS.muhurtham, MARKS.POURNAMI, MARKS.AMAVASAI] as const).map((mark) => (
                  <Stack key={mark.title} direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ color: mark.colour, display: 'flex', fontSize: 13 }}>
                      {mark.symbol}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {mark.title}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {isLoading ? <LoadingBlock rows={5} /> : null}

              <Box
                sx={{
                  display: 'grid',
                  // Square days: the columns are held to the cell height rather
                  // than stretched across the card, so the month reads as a grid
                  // of dates instead of a row of wide bands.
                  gridTemplateColumns: `repeat(7, ${DAY_CELL_HEIGHT}px)`,
                  justifyContent: 'start',
                  gap: `${DAY_GRID_GAP}px`,
                }}
              >
                {WEEKDAYS.map((label) => (
                  <Typography
                    key={label}
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      pb: 0.25,
                      // Sunday in red, as the wall calendar beside the desk
                      // already has it.
                      color: label === 'Sun' ? 'error.main' : 'text.secondary',
                    }}
                  >
                    {label}
                  </Typography>
                ))}

                {days.map((day) => {
                  const dayOrders = ordersOn(day)
                  const muhurtham = muhurthamOn(day)
                  const tamil = tamilOn(day)
                  const outside = !isSameMonth(day, month)
                  const isSelected = selected != null && isSameDay(day, selected)
                  return (
                    <Box
                      key={day.toISOString()}
                      onClick={() => setSelected(day)}
                      // The cell is a control - it picks the day the panel below
                      // reads - so it says so, and says which day it picks.
                      role="button"
                      aria-pressed={isSelected}
                      aria-label={format(day, 'd MMMM yyyy')}
                      title={[
                        tamil?.moonDay ? tamil.moonDayLabel : null,
                        muhurtham ? muhurtham.label ?? 'Muhurtham' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || undefined}
                      sx={{
                        minHeight: DAY_CELL_HEIGHT,
                        p: 0.45,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: (theme) =>
                          `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                        // A muhurtham day is tinted so peak demand reads at a glance.
                        // The palette has no 50 shade, so tint the secondary itself.
                        bgcolor: (theme) =>
                          isSelected
                            ? theme.palette.action.selected
                            : muhurtham
                              ? alpha(theme.palette.secondary.main, 0.14)
                              : sandal(theme),
                        borderLeft: (theme) =>
                          muhurtham ? `3px solid ${theme.palette.secondary.main}` : undefined,
                        opacity: outside ? 0.45 : 1,
                        '&:hover': { borderColor: 'primary.light' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: 15,
                            lineHeight: 1.2,
                            fontWeight: isToday(day) ? 800 : 600,
                            // Sunday wins over today: the red says what the day
                            // is, and today already carries the heavier weight.
                            color: isSunday(day)
                              ? 'error.main'
                              : isToday(day)
                                ? 'primary.main'
                                : 'text.primary',
                          }}
                        >
                          {format(day, 'd')}
                        </Typography>
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          {tamil?.moonDay ? (
                            <Box
                              title={MARKS[tamil.moonDay].title}
                              sx={{ color: MARKS[tamil.moonDay].colour, display: 'flex', fontSize: 11 }}
                            >
                              {MARKS[tamil.moonDay].symbol}
                            </Box>
                          ) : null}
                          {muhurtham ? (
                            <Box
                              title={MARKS.muhurtham.title}
                              sx={{ color: MARKS.muhurtham.colour, display: 'flex', fontSize: 13 }}
                            >
                              {MARKS.muhurtham.symbol}
                            </Box>
                          ) : null}
                        </Stack>
                      </Stack>

                      {/* How busy the day is, and no more than that. Who the
                          booking is for belongs under the calendar, where it is
                          read deliberately rather than over someone's shoulder. */}
                      {dayOrders.length > 0 ? (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'inline-block',
                            mt: 0.25,
                            px: 0.4,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                            fontSize: 10,
                            lineHeight: 1.5,
                          }}
                        >
                          {dayOrders.length === 1 ? '1 order' : `${dayOrders.length} orders`}
                        </Typography>
                      ) : null}
                    </Box>
                  )
                })}
              </Box>

            </CardContent>
          </Card>
        </Grid>

        {/* ------------------------------------------------------- upcoming */}
        <Grid item xs={12} lg>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1">
                  {selected ? format(selected, 'EEEE d MMMM') : 'Pick a day'}
                </Typography>
                {/* Only the muhurtham, which is a day the halls fill. The moon
                    is already drawn on the date in the calendar, and saying it
                    again beside the heading told nobody anything new.

                    The mark goes in as the icon rather than into the label: it
                    is a drawing, and a drawing put into a line of text is
                    printed as [object Object]. */}
                {selectedMuhurtham ? (
                  <Chip
                    size="small"
                    color="secondary"
                    icon={MARKS.muhurtham.symbol}
                    label={selectedMuhurtham.label ?? 'Muhurtham'}
                  />
                ) : null}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Every order on this day
              </Typography>

              {dayEvents.length === 0 ? (
                <EmptyState message="Nothing booked on this day." />
              ) : (
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  {dayEvents.slice(0, 12).map((order) => (
                    <Box
                      key={order.id}
                      onClick={() => navigate(`/orders/${order.id}`)}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                        // A cancelled event stays readable but stops competing
                        // with the bookings that are still standing.
                        opacity: order.status === 'CANCELLED' ? 0.65 : 1,
                        '&:hover': { borderColor: 'primary.light' },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{order.customerName}</Typography>
                        <StatusChip status={order.status} />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {order.orderNumber} · {order.guestCount} guests
                        </Typography>
                        {/* Money is only worth reading on a booking that stands;
                            what a cancelled one owes or is owed comes out of the
                            cancellation charge, not this balance. */}
                        {order.status === 'CANCELLED' ? null : order.balanceAmount > 0 ? (
                          <Typography variant="caption" color="warning.main">
                            {formatMoney(order.balanceAmount)} due
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="success.main">
                            paid
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* The money and the calls sit on their own row: the calendar is only as
          wide as the month, and the panel beside it takes the rest. */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        {/* ---------------------------------------------------- outstanding */}
        {can('billing.read') ? (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Outstanding
                </Typography>
                {(outstanding ?? []).length === 0 ? (
                  <EmptyState message="Nothing outstanding." />
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Over 90 days</TableCell>
                        <TableCell>Oldest due</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(outstanding ?? []).slice(0, 10).map((row) => (
                        <TableRow key={row.customerId} hover>
                          <TableCell>
                            {row.customerName}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatMobile(row.mobile)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatMoney(row.totalOutstanding)}</TableCell>
                          <TableCell align="right">
                            {row.over90 > 0 ? (
                              <Typography variant="body2" color="error.main">
                                {formatMoney(row.over90)}
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>{formatDate(row.oldestDueDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>
        ) : null}

        {/* ----------------------------------------------------- follow-ups */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">Enquiry follow-ups</Typography>
                <Button size="small" onClick={() => navigate('/enquiries')}>
                  All enquiries
                </Button>
              </Stack>

              {(followups ?? []).length === 0 ? (
                <EmptyState message="No follow-ups due in the next week." />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Due</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Enquiry</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(followups ?? []).slice(0, 10).map((followup) => {
                      const overdue =
                        followup.scheduledAt != null && new Date(followup.scheduledAt) < new Date()
                      return (
                        <TableRow
                          key={followup.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/enquiries/${followup.enquiryId}`)}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={overdue ? 'error.main' : 'text.primary'}
                            >
                              {formatDateTime(followup.scheduledAt)}
                            </Typography>
                            {overdue ? (
                              <Typography variant="caption" color="error.main">
                                overdue
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {followup.customerName ?? '—'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatMobile(followup.customerMobile)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {followup.enquiryNumber ?? '—'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              event {formatDate(followup.eventDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" label={humanise(followup.followupType)} />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}
